import { useCustomQuery, useGetQuery, useFindByQuery } from '../../graphql';
import { RoutineWorkflow, WorkspaceRoutine } from '../../routine';
import { useEmbeddedRoutines } from './useEmbeddedRoutines';
import { useWorkspaceAsset } from '../../assets/hooks/useWorkspaceAsset';
import { useMemo } from 'react';

/**
 * The alerts in `workflows` that prompt `workspaceRoutineId`. Both the asset's
 * own alerts and the ones it inherits from its groups come back unfiltered
 * (the workflow filter can't reach into `trigger.config`), so both are narrowed
 * here by the same rule.
 */
const forRoutine = (
  workflows: RoutineWorkflow[] | undefined,
  workspaceRoutineId: string
): RoutineWorkflow[] =>
  (workflows ?? []).filter(
    (workflow) =>
      workflow?.trigger?.config?.workspaceRoutineId === workspaceRoutineId
  );

export const useRoutineAlerts = (
  workspaceAssetId: string,
  workspaceRoutineId: string,
  productId?: string,
  options?: { enabled?: boolean }
) => {
  const isProductMode = !workspaceAssetId && !!productId;
  const enabled =
    (options?.enabled ?? true) && (!!workspaceAssetId || isProductMode);
  const assetEnabled = enabled && !isProductMode;

  const { data: workspaceAssetData, isLoading: isWorkspaceAssetIdLoading } =
    useGetQuery({
      modelName: 'workspaceasset',
      query: GET_WORKSPACE_ASSET_FOR_ASSET_ID,
      id: workspaceAssetId,
      options: { enabled: assetEnabled },
    });

  const assetId = (workspaceAssetData as any)?.asset?.id;

  const { workspaceAsset: wsAsset, isLoading: isWorkspaceAssetLoading } =
    useWorkspaceAsset(assetId || '', assetId ? false : true);

  // Scoped key avoids collision with `useWorkspaceRoutine`'s
  // `['workspaceroutine', 'details', id]`, which routine submit depends on
  // (GRIPP-2801). Keeping 'workspaceroutine' as the first segment ensures
  // routine mutations that invalidate cacheKeys('WorkspaceRoutine').all
  // also invalidate this query.
  const {
    data: workspaceRoutineResponse,
    isLoading: isWorkspaceRoutineLoading,
  } = useCustomQuery({
    cacheKey: ['workspaceroutine', 'routineAlerts', workspaceRoutineId],
    query: GET_WORKSPACE_ROUTINE,
    variables: { id: workspaceRoutineId },
    options: { enabled },
  });

  const workspaceRoutineData = (
    workspaceRoutineResponse as
      | { getWorkspaceRoutine?: WorkspaceRoutine }
      | undefined
  )?.getWorkspaceRoutine;

  const { data: routineWorkflowData, isLoading: isWorkflowLoading } =
    useFindByQuery({
      modelName: 'workflow',
      query: FIND_WORKFLOWS,
      variables: {
        filter: isProductMode
          ? { product: { eq: productId } }
          : { workspaceAsset: { eq: workspaceAssetId } },
      },
      options: { enabled },
    });

  const groupIds = useMemo(
    () =>
      (wsAsset?.groups ?? [])
        .map((group) => group?.id)
        .filter((id): id is string => !!id),
    [wsAsset?.groups]
  );

  const { data: groupWorkflowData, isLoading: isGroupWorkflowLoading } =
    useFindByQuery({
      modelName: 'workflow',
      query: FIND_WORKFLOWS,
      variables: { filter: { group: { in: groupIds } } },
      options: { enabled: enabled && groupIds.length > 0 },
    });

  // Merge all routines
  const allRoutines = useMemo(() => {
    const routines = [
      ...(wsAsset?.routines || []),
      ...(wsAsset?.groups?.flatMap((g: any) => g.routines || []) || []),
      ...(wsAsset?.asset?.product?.routines || []),
    ];

    const seen = new Set<string>();
    return routines.filter((r) => r?.id && !seen.has(r.id) && seen.add(r.id));
  }, [wsAsset]);

  const { routinesWithEmbedded, isEmbeddedRoutinesLoading } =
    useEmbeddedRoutines(allRoutines);

  const routineWorkflows = useMemo(
    () => forRoutine(routineWorkflowData?.items, workspaceRoutineId),
    [routineWorkflowData?.items, workspaceRoutineId]
  );

  /**
   * Alerts the asset inherits from its groups, kept separate from
   * `routineWorkflows` rather than merged into it: they are read-only at the
   * asset level (edited on the group), and callers that treat every entry as
   * editable — the mobile edit screen looks its alert up in `routineWorkflows`
   * — must not pick them up.
   */
  const groupWorkflows = useMemo(
    () => forRoutine(groupWorkflowData?.items, workspaceRoutineId),
    [groupWorkflowData?.items, workspaceRoutineId]
  );

  const isLoading = isProductMode
    ? isWorkspaceRoutineLoading || isWorkflowLoading
    : isWorkspaceAssetIdLoading ||
      isWorkspaceAssetLoading ||
      isWorkspaceRoutineLoading ||
      isWorkflowLoading ||
      isGroupWorkflowLoading ||
      isEmbeddedRoutinesLoading;

  const workflowState = (workspaceAssetData as any)?.workflowState;

  const memoizedWorkspaceAsset = useMemo(() => {
    if (!wsAsset) return undefined;
    return {
      ...wsAsset,
      routines: routinesWithEmbedded,
      ...(workflowState ? { workflowState } : {}),
    };
  }, [wsAsset, routinesWithEmbedded, workflowState]);

  return {
    workspaceAsset: memoizedWorkspaceAsset,
    workspaceRoutine: workspaceRoutineData as WorkspaceRoutine,
    routineWorkflows,
    groupWorkflows,
    isLoading,
  };
};

const GET_WORKSPACE_ASSET_FOR_ASSET_ID = `
  query getWorkspaceAsset($id: ID!) {
    getWorkspaceAsset(id: $id) {
      id
      asset {
        id
      }
      workflowState {
        workflowId
        triggerState
        actionState
      }
    }
  }
`;

export const GET_WORKSPACE_ROUTINE = `
  query getWorkspaceRoutine($id: ID!) {
    getWorkspaceRoutine(id: $id) {
      id
      name
      workspace {
        id
      }
      type
      config
      currentVersion {
        id
      }
    }
  }
`;

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
        group {
          id
          name
        }
      }
    }
}
`;
